import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { RowSkeleton } from "@/components/skeleton/RowSkeleton";
import { useDebounceState } from "@/hooks/useDebounceState";
import { SearchInput } from "@/pages/dashboard/SearchInput";
import { createColumnHelper } from "@tanstack/react-table";
import { appActions } from "@/redux/actions/app.actions";
import { appSelector } from "@/redux/selectors/app.selector";
import { useDispatch, useSelector } from "@/redux/store";
import { SETTINGS_FORM_STACK_CLASS, SETTINGS_TABLE_WRAPPER } from "@/pages/settings/settings-layout-constants";
import { SettingsTableToolbar } from "@/pages/settings/SettingsTableToolbar";
import { UnsavedChangesBar } from "@/components/settings/UnsavedChangesBar";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import { Plus, Trash2 } from "lucide-react";
import AuthProvider from "@/providers/auth.provider";
import { toast } from "sonner";
import { AddOfficeDialog } from "./AddOfficeDialog";
import { FindXactnetAddressDialog } from "./FindXactnetAddressDialog";
import { InlineEditCell } from "./InlineEditCell";

function padXnRowValues(officeCount: number, xns: string[]): string[] {
  return Array.from({ length: officeCount }, (_, i) => (typeof xns[i] === "string" ? xns[i] : ""));
}

function createOfficeRowId() {
  return `office-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function buildOfficeRows(offices: string[], xns: string[]): OfficeTableRow[] {
  const paddedXns = padXnRowValues(offices.length, xns);
  return offices.map((name, i) => ({
    id: `office-${i}-${String(name).trim() || "unnamed"}`,
    name: String(name),
    xn: paddedXns[i] ?? "",
  }));
}

function validateOfficesForSave(names: string[]): string | null {
  const trimmed = names.map((name) => name.trim());
  if (trimmed.some((name) => name.length === 0)) {
    return "Each office must have a non-empty name.";
  }

  const normalized = trimmed.map((name) => name.toLowerCase());
  if (new Set(normalized).size !== normalized.length) {
    return "Office names must be unique.";
  }

  return null;
}

// Shown when an office would be left without a Xactnet address and the org has no default.
const XN_RULE_MESSAGE = "Add an organization default, or give every office a Xactnet address.";

// Rule: unless the org has a default Xactnet address, every office must have a non-empty one.
function xnRuleSatisfied(defaultXactnetAddress: string, xns: string[]): boolean {
  if (defaultXactnetAddress.trim().length > 0) return true;
  return xns.every((xn) => xn.trim().length > 0);
}

const DEFAULT_OFFICES_PAGE_SIZE = 25;

type OfficeTableRow = {
  id: string;
  name: string;
  xn: string;
};

const officeColumnHelper = createColumnHelper<OfficeTableRow>();

export function OfficesSettingsPage() {
  const dispatch = useDispatch();
  const user = useSelector(appSelector.user);
  const serverOffices = useSelector(appSelector.organizationOfficeNames);
  const serverXn = useMemo(() => {
    const raw = user?.extraData?.customSettings?.XNAddresses;
    return Array.isArray(raw) ? raw : [];
  }, [user?.extraData?.customSettings?.XNAddresses]);
  const serverXnSerialized = useMemo(() => JSON.stringify(serverXn), [serverXn]);
  const serverOfficesSerialized = useMemo(() => JSON.stringify(serverOffices), [serverOffices]);
  const [refetching, setRefetching] = useState(false);
  const [orgWideXn, setOrgWideXn] = useState("");
  const [officeRows, setOfficeRows] = useState<OfficeTableRow[]>([]);
  const [deleteRow, setDeleteRow] = useState<OfficeTableRow | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [immediateSaving, setImmediateSaving] = useState(false);
  const [dirtyTrackingReady, setDirtyTrackingReady] = useState(false);
  // Set just before an immediate add/delete refetch so the next server-driven
  // useEffect doesn't overwrite the locally-spliced officeRows (which may
  // contain unrelated pending name/XN edits the user hasn't saved yet).
  const skipNextServerSyncRef = useRef(false);

  const [officeSearch, setOfficeSearch] = useState("");
  const debouncedOfficeSearch = useDebounceState(officeSearch, 300);
  const [officesPageSize, setOfficesPageSize] = useState(DEFAULT_OFFICES_PAGE_SIZE);

  const refetchUserDetails = useCallback(async () => {
    const firebaseUser = getAuth().currentUser;
    if (!firebaseUser) return;
    const firebaseToken = await firebaseUser.getIdToken(false);
    await dispatch(appActions.getUserDetails(firebaseUser, firebaseToken));
  }, [dispatch]);

  const refetchOnceRef = useRef(false);

  useEffect(() => {
    if (skipNextServerSyncRef.current) {
      skipNextServerSyncRef.current = false;
      setDirtyTrackingReady(true);
      return;
    }
    setOfficeRows(buildOfficeRows(serverOffices, serverXn));
    setDirtyTrackingReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- serialized deps prevent re-sync on reference-only changes
  }, [serverOfficesSerialized, serverXnSerialized]);

  useEffect(() => {
    setOrgWideXn(user?.extraData?.recipientsXNAddress ?? "");
  }, [user?.extraData?.recipientsXNAddress]);

  useEffect(() => {
    const hasOfficeList = Boolean(user?.extraData && Array.isArray(user.extraData.officeList));
    if (!user || hasOfficeList || refetchOnceRef.current) return;

    refetchOnceRef.current = true;
    let cancelled = false;
    void (async () => {
      setRefetching(true);
      try {
        await refetchUserDetails();
      } catch (e) {
        console.error("OfficesSettingsPage: getUserDetails refetch failed", e);
      } finally {
        if (!cancelled) setRefetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, refetchUserDetails]);

  const handleSaveOfficeName = useCallback((rowId: string, value: string) => {
    setOfficeRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, name: value } : row)));
  }, []);

  const handleSaveXn = useCallback((rowId: string, value: string) => {
    setOfficeRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, xn: value } : row)));
  }, []);

  // Immediate save against the server baseline. Used by add/delete only.
  // Intentionally does NOT include the user's pending draft edits to other
  // rows — those stay local until they hit Save changes on the bar.
  const saveOfficesImmediate = useCallback(
    async (nextOffices: string[], nextXn: string[]): Promise<boolean> => {
      const orgId = user?.organizationId;
      if (!orgId) {
        toast.error("Missing organization.");
        return false;
      }

      const validationError = validateOfficesForSave(nextOffices);
      if (validationError) {
        toast.error(validationError);
        return false;
      }

      // This path writes the saved org default (not the unsaved draft), so the rule
      // is checked against that same value — an unsaved default doesn't relax it.
      if (!xnRuleSatisfied(user?.extraData?.recipientsXNAddress ?? "", nextXn)) {
        toast.error(XN_RULE_MESSAGE);
        return false;
      }

      setImmediateSaving(true);
      try {
        const payload = {
          organizationId: orgId,
          recipientsXNAddress: (user?.extraData?.recipientsXNAddress ?? "").trim(),
          customSettings: JSON.stringify({
            offices: nextOffices.map((o) => o.trim()),
            XNAddresses: nextXn.map((x) => x.trim()),
          }),
        };

        const res = await AuthProvider.editOrganization(payload);
        if (!res.success) {
          toast.error(res.error ?? "Could not save changes.");
          return false;
        }
        skipNextServerSyncRef.current = true;
        await refetchUserDetails();
        return true;
      } catch (e) {
        console.error(e);
        toast.error("Could not save changes.");
        return false;
      } finally {
        setImmediateSaving(false);
      }
    },
    [refetchUserDetails, user?.extraData?.recipientsXNAddress, user?.organizationId],
  );

  const handleConfirmAdd = useCallback(
    async (name: string, xn: string) => {
      const nextOffices = [...serverOffices, name];
      const nextXn = [...padXnRowValues(serverOffices.length, serverXn), xn];
      const ok = await saveOfficesImmediate(nextOffices, nextXn);
      if (!ok) return;

      setOfficeRows((prev) => [...prev, { id: createOfficeRowId(), name, xn }]);
      setAddDialogOpen(false);
      toast.success("Office added.");
    },
    [saveOfficesImmediate, serverOffices, serverXn],
  );

  const handleConfirmDelete = async () => {
    if (!deleteRow) return;

    const serverIndex = serverOffices.findIndex((o) => o.trim().toLowerCase() === deleteRow.name.trim().toLowerCase());
    if (serverIndex === -1) {
      setOfficeRows((prev) => prev.filter((row) => row.id !== deleteRow.id));
      setDeleteRow(null);
      return;
    }

    const paddedXn = padXnRowValues(serverOffices.length, serverXn);
    const nextOffices = serverOffices.filter((_, i) => i !== serverIndex);
    const nextXn = paddedXn.filter((_, i) => i !== serverIndex);

    const ok = await saveOfficesImmediate(nextOffices, nextXn);
    if (!ok) return;

    setOfficeRows((prev) => prev.filter((row) => row.id !== deleteRow.id));
    setDeleteRow(null);
    toast.success("Office deleted.");
  };

  const onDiscard = useCallback(() => {
    setOfficeRows(buildOfficeRows(serverOffices, serverXn));
    setOrgWideXn(user?.extraData?.recipientsXNAddress ?? "");
  }, [serverOffices, serverXn, user?.extraData?.recipientsXNAddress]);

  const onSave = async () => {
    const orgId = user?.organizationId;
    if (!orgId) {
      toast.error("Missing organization.");
      return;
    }

    const offices = officeRows.map((row) => row.name.trim());
    const validationError = validateOfficesForSave(offices);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    const XNAddresses = officeRows.map((row) => row.xn.trim());
    if (!xnRuleSatisfied(orgWideXn, XNAddresses)) {
      toast.error(XN_RULE_MESSAGE);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        organizationId: orgId,
        recipientsXNAddress: orgWideXn.trim(),
        customSettings: JSON.stringify({ offices, XNAddresses }),
      };

      const res = await AuthProvider.editOrganization(payload);
      if (!res.success) {
        toast.error(res.error ?? "Could not save settings.");
        return;
      }
      toast.success("Saved.");
      await refetchUserDetails();
    } catch (e) {
      console.error(e);
      toast.error("Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  const serverBaselineFingerprint = useMemo(() => {
    const offices = serverOffices.map((office) => office.trim());
    const xn = padXnRowValues(serverOffices.length, serverXn).map((value) => value.trim());
    const org = (user?.extraData?.recipientsXNAddress ?? "").trim();
    return JSON.stringify({ offices, xn, org });
  }, [serverOffices, serverXn, user?.extraData?.recipientsXNAddress]);

  const draftFingerprint = useMemo(() => {
    const offices = officeRows.map((row) => row.name.trim());
    const xn = officeRows.map((row) => row.xn.trim());
    const org = orgWideXn.trim();
    return JSON.stringify({ offices, xn, org });
  }, [officeRows, orgWideXn]);

  const isDirty = dirtyTrackingReady && draftFingerprint !== serverBaselineFingerprint;

  // Bulk save writes the draft, so evaluate the rule against the draft (rows + draft default).
  const draftXnRuleOk = xnRuleSatisfied(
    orgWideXn,
    officeRows.map((row) => row.xn),
  );
  // The Add dialog gates on the *saved* org default: an unsaved default doesn't exist server-side
  // yet, and adds write immediately, so a blank-xn add must still be blocked until it's saved.
  const requireOfficeXn = (user?.extraData?.recipientsXNAddress ?? "").trim().length === 0;

  const filteredOfficeTableRows = useMemo(() => {
    const q = debouncedOfficeSearch.trim().toLowerCase();
    if (!q) return officeRows;
    return officeRows.filter((r) => r.name.toLowerCase().includes(q) || r.xn.toLowerCase().includes(q));
  }, [officeRows, debouncedOfficeSearch]);

  const officeColumns = useMemo(
    () => [
      officeColumnHelper.display({
        id: "name",
        header: "Name",
        cell: ({ row }) => (
          <InlineEditCell
            rowId={row.original.id}
            displayValue={row.original.name}
            onSave={handleSaveOfficeName}
            ariaLabel="Office name"
            placeholder="Click to add name"
            title="Click to edit office name"
          />
        ),
        meta: { bodyClassName: "!w-[13rem] !min-w-[13rem] !max-w-[13rem]" },
      }),
      officeColumnHelper.display({
        id: "xn",
        header: "Xactnet address",
        cell: ({ row }) => (
          <InlineEditCell
            rowId={row.original.id}
            displayValue={row.original.xn}
            onSave={handleSaveXn}
            ariaLabel="Xactnet address"
            placeholder="Click to add address"
            title="Click to edit Xactnet address"
          />
        ),
        meta: { bodyClassName: "!w-[13rem] !min-w-[13rem] !max-w-[13rem]" },
      }),
      officeColumnHelper.display({
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="flex">
            <Button type="button" variant="ghost" size="icon" aria-label="Delete office" onClick={() => setDeleteRow(row.original)}>
              <Trash2 className="size-4" />
            </Button>
          </div>
        ),
        meta: { bodyClassName: "!w-20 !min-w-20 !max-w-20" },
      }),
    ],
    [handleSaveOfficeName, handleSaveXn],
  );

  const openAddDialog = () => {
    setOfficeSearch("");
    setAddDialogOpen(true);
  };

  const officesTableToolbar = (
    <SettingsTableToolbar>
      <SearchInput
        value={officeSearch}
        onSearch={setOfficeSearch}
        placeholder="Search offices…"
        wrapperClassName="min-w-0 flex-1 basis-[12rem] max-w-md shrink"
      />
      <Button type="button" variant="outline" size="sm" className="shrink-0 gap-1 sm:ml-auto" onClick={openAddDialog}>
        <Plus className="size-4" />
        Add office
      </Button>
    </SettingsTableToolbar>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto pb-20">
      <div className="border-border flex flex-1 flex-col gap-6">
        {refetching ? (
          <div className={SETTINGS_TABLE_WRAPPER}>
            <p className="text-muted-foreground p-6 text-sm">Loading office list…</p>
          </div>
        ) : (
          <>
            <div className={cn("flex flex-col gap-2", SETTINGS_FORM_STACK_CLASS)}>
              <label htmlFor="org-xn" className="text-foreground text-base font-semibold">
                Organization Default Xactnet address
              </label>
              <div className="relative">
                <Input
                  id="org-xn"
                  value={orgWideXn}
                  onChange={(e) => setOrgWideXn(e.target.value)}
                  placeholder="e.g. COMPANY.XACTNET.COM"
                  autoComplete="off"
                  className="pr-10"
                />
                <FindXactnetAddressDialog className="absolute top-1/2 right-3 -translate-y-1/2" />
              </div>
              <p className="text-muted-foreground text-xs">Used when no office-specific addresses are applicable.</p>
            </div>

            <div className="flex w-full flex-col gap-2">
              <h2 className="text-foreground text-base font-semibold">Offices</h2>

              {officeRows.length === 0 ? (
                <div className={SETTINGS_TABLE_WRAPPER}>
                  <div className="flex flex-col items-start gap-3 p-6">
                    <p className="text-muted-foreground text-sm">No offices configured.</p>
                    <Button type="button" variant="outline" size="sm" className="gap-1" onClick={openAddDialog}>
                      <Plus className="size-4" />
                      Add office
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={cn(SETTINGS_TABLE_WRAPPER, "flex flex-col overflow-hidden p-0")}>
                  {officesTableToolbar}
                  <DataTable<OfficeTableRow>
                    columns={officeColumns}
                    data={filteredOfficeTableRows}
                    embedded
                    isLoading={false}
                    pageSize={officesPageSize}
                    onPageSizeChange={setOfficesPageSize}
                    renderSkeleton={() => <RowSkeleton />}
                    emptyState={
                      <span className="text-muted-foreground text-sm">
                        No offices match your search. Try another name or Xactnet address.
                      </span>
                    }
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <AddOfficeDialog
        open={addDialogOpen}
        existingNames={officeRows.map((row) => row.name)}
        requireXn={requireOfficeXn}
        isSaving={immediateSaving}
        onCancel={() => setAddDialogOpen(false)}
        onConfirm={handleConfirmAdd}
      />

      <AlertDialog open={!!deleteRow} onOpenChange={(open) => !open && !immediateSaving && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete office?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRow
                ? `This will delete "${deleteRow.name.trim() || "Untitled office"}" and its paired Xactnet address. Any associated estimates will revert to a blank office assignment. This can't be undone.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={immediateSaving}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              className="border-destructive/40"
              onClick={() => void handleConfirmDelete()}
              disabled={immediateSaving}
            >
              {immediateSaving ? "Deleting…" : "Delete office"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UnsavedChangesBar
        isDirty={isDirty}
        isSaving={saving}
        canSave={draftXnRuleOk}
        message={draftXnRuleOk ? undefined : XN_RULE_MESSAGE}
        onSave={() => void onSave()}
        onDiscard={onDiscard}
      />
    </div>
  );
}
